import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { HotkeysProvider } from '@tanstack/react-hotkeys';
import { ScratchFeedback } from '@fldr/scratch';
import { ToastProvider } from '@trove/ui';
import '@fontsource/geist-sans/latin-400.css';
import '@fontsource/geist-sans/latin-500.css';
import '@fontsource/geist-sans/latin-600.css';
import '@fontsource/geist-mono/latin-400.css';
import '@fontsource/geist-mono/latin-500.css';
import '@trove/ui/tokens.css';
import { AnalyticsProvider, PostHogIdentify, posthogEnabled } from './analytics';
import { routeTree } from './routeTree.gen';
import { SessionProvider } from './session';
import { WorkspaceProvider } from './workspace';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 10_000, retry: false },
    mutations: { retry: false },
  },
});

const router = createRouter({ routeTree, context: { queryClient } });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AnalyticsProvider>
            <SessionProvider>
              {posthogEnabled ? <PostHogIdentify /> : null}
              <WorkspaceProvider>
                <HotkeysProvider>
                  <RouterProvider router={router} context={{ queryClient }} />
                </HotkeysProvider>
              </WorkspaceProvider>
            </SessionProvider>
          </AnalyticsProvider>
        </ToastProvider>
      </QueryClientProvider>
      <ScratchFeedback isDevelopment={import.meta.env.DEV} feedbackUrl="/v1/feedback" />
    </>
  </React.StrictMode>,
);
